"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { neueHeitz } from "../global-fonts";
import { useEffect, useRef, useState } from "react";

const shopNavigation = [
  { label: "STORIES", href: "/test/stories" },
  { label: "RECIPES", href: "/test/recipes" },
  { label: "PRODUCTS", href: "/test/products" },
] as const;

export default function Header() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const mobileShopMenuRef = useRef<HTMLDetailsElement>(null);
  const desktopShopMenuRef = useRef<HTMLDetailsElement>(null);

  const isShopItemActive = (href: string) =>
    pathname === href || pathname?.startsWith(`${href}/`) === true;
  const isShopActive = shopNavigation.some(({ href }) =>
    isShopItemActive(href),
  );

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node;

      if (!mobileShopMenuRef.current?.contains(target)) {
        mobileShopMenuRef.current?.removeAttribute("open");
      }
      if (!desktopShopMenuRef.current?.contains(target)) {
        desktopShopMenuRef.current?.removeAttribute("open");
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  function handleShopSelection() {
    mobileShopMenuRef.current?.removeAttribute("open");
    desktopShopMenuRef.current?.removeAttribute("open");
    setIsOpen(false);
  }

  function handleMenuToggle(isOpen: boolean): void {
    setIsOpen(!isOpen);
  }
  return (
    <header
      className={`flex 
        items-center 
        justify-between 
        pt-4 
        pb-4 
        font-[family-name:var(--font-alegreya-sans)] 
        font-black 
        text-xl 
        sticky 
        sticky-top 
        top-0
        bg-[url(/images/burgundy-textured-background.png)] 
        tracking-widest 
        flex-nowrap 
        content-center
        text-sand
        z-1000`}
    >
      <Link
        href="/"
        className={`${neueHeitz.className} 
        hidden 
        2xl:block 
        2xl:ml-24 
        flex-none 
        2xl:text-fluid-base
        `}
      >
        REVOLUTIONARY GRAINS
      </Link>
      <Link
        href="/"
        className={`${neueHeitz.className} 
          pl-12
          text-fluid-xl
          lg:shrink-0
          lg:pl-8
          lg:text-3xl
          2xl:hidden
          rg-link`}
      >
        RG
      </Link>
      <button
        className={`lg:hidden 
          bg-[url(/images/mobile-nav-menu.png)] 
          z-1001 
          w-12 
          h-12 
          aspect-square 
          no-repeat 
          bg-contain 
          mr-16`}
        onClick={() => handleMenuToggle(isOpen)}
      >
        <span className="sr-only">Open Menu</span>
      </button>
      {/* Mobile Navigation Menu */}
      <div
        data-is-open={isOpen ? "true" : "false"}
        className={`fixed 
          inset-y-0 
          inset-x-1/3 
          right-0 
          lg:hidden 
          pt-4 
          bg-[url(/images/burgundy-textured-background.png)] 
          lg:hidden 
          motion-safe:transition 
          transition-transform 
          data-[is-open=false]:translate-x-full 
          z-1000`}
      >
        <ul className="space-y-4 pl-16 pt-32">
          <li>
            <Link href="/test/map" onClick={() => handleMenuToggle(isOpen)}>
              SOLIDARITY LATTICE
            </Link>
          </li>
          <li>
            <Link
              href="/test/community-wall"
              onClick={() => handleMenuToggle(isOpen)}
            >
              COMMUNITY WALL
            </Link>
          </li>
          <li>
            <details ref={mobileShopMenuRef}>
              <summary
                className={`cursor-pointer list-none [&::-webkit-details-marker]:hidden ${
                  isShopActive ? "border-b-2 border-sand pb-1" : ""
                }`}
              >
                SHOP
              </summary>
              <ul className="space-y-3 pt-3 pl-4 text-base">
                {shopNavigation.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      aria-current={
                        isShopItemActive(item.href) ? "page" : undefined
                      }
                      className={
                        isShopItemActive(item.href)
                          ? "border-b-2 border-sand pb-1"
                          : "rg-link"
                      }
                      onClick={handleShopSelection}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </details>
          </li>
          <li>
            <Link href="/#projects" onClick={() => handleMenuToggle(isOpen)}>
              PROJECTS
            </Link>
          </li>
          <li>
            <Link href="/our-roots" onClick={() => handleMenuToggle(isOpen)}>
              OUR ROOTS
            </Link>
          </li>
          <li>
            <Link href="/join-us" onClick={() => handleMenuToggle(isOpen)}>
              JOIN US
            </Link>
          </li>
        </ul>
      </div>

      <ul className="mr-4 ml-4 hidden min-w-0 flex-1 place-content-evenly whitespace-nowrap text-sm lg:flex xl:mr-8 xl:ml-8 xl:text-base 2xl:text-xl">
        <li>
          <Link href="/test/map" className="rg-link">
            SOLIDARITY LATTICE
          </Link>
        </li>
        <li>
          <Link href="/test/community-wall" className="rg-link">
            COMMUNITY WALL
          </Link>
        </li>
        <li className="relative">
          <details ref={desktopShopMenuRef} className="group">
            <summary
              className={`rg-link cursor-pointer list-none [&::-webkit-details-marker]:hidden ${
                isShopActive ? "border-b-2 border-sand pb-1" : ""
              }`}
            >
              SHOP
            </summary>
            <ul className="invisible absolute top-full left-1/2 min-w-40 -translate-x-1/2 space-y-2 bg-[url(/images/burgundy-textured-background.png)] px-5 py-4 opacity-0 shadow-xl transition-[opacity,visibility] group-open:visible group-open:opacity-100">
              {shopNavigation.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    aria-current={
                      isShopItemActive(item.href) ? "page" : undefined
                    }
                    className={
                      isShopItemActive(item.href)
                        ? "border-b-2 border-sand pb-1"
                        : "rg-link"
                    }
                    onClick={handleShopSelection}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </details>
        </li>
        <li>
          <Link href="/#projects" className={`rg-link`}>
            PROJECTS
          </Link>
        </li>
        <li>
          <Link href="/our-roots" className={`rg-link`}>
            OUR ROOTS
          </Link>
        </li>
        <li>
          <Link href="/join-us" className={`rg-link`}>
            JOIN US
          </Link>
        </li>
      </ul>
    </header>
  );
}
